import { useMemo } from 'react';
import { shouldUseEventDesignDraft } from '@apps/guest/routes/GuestSite/GuestSite.utils';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { DesignLayoutType, EventDesignPurpose, EventPageFragment, useGuestSiteNavBarQuery } from '@graphql/generated';
import { isPast } from 'date-fns';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';

interface NavBarControllerProps {
  eventHandle: string;
  layout?: DesignLayoutType;
}

export const useNavBarController = ({ eventHandle, layout }: NavBarControllerProps) => {
  const { data } = useGuestSiteNavBarQuery({
    variables: {
      eventHandle,
      eventDesignPurpose: shouldUseEventDesignDraft() ? EventDesignPurpose.draft : EventDesignPurpose.live,
    },
    batchMode: 'fast',
  });
  const { pages: guestSitePages } = useGetGuestSitePages({
    pages: data?.eventByName?.pages as EventPageFragment[],
    layoutType: data?.eventByName?.eventDesign?.websiteLayout.layoutType,
  });
  const event = data?.eventByName;
  const info = data?.eventByName?.info;
  const finalizedDateMs = info?.finalizedDate?.milliseconds || 0;
  const isEventPast = isPast(finalizedDateMs);
  const { theme } = useEventDesignThemingProvider();
  const isSmallScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm4' }));
  const navBarVariant: 'menu' | 'inline' = isSmallScreen || layout === DesignLayoutType.aloha ? 'menu' : 'inline';

  const navbarEventProps = useMemo(() => {
    return {
      eventId: event?.id || '',
      layoutType: event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha,
      dateInMilliseconds: info?.dateV0918?.milliseconds || 0,
      hasEventUnlocked: event?.activeSession.hasUnlocked || false,
      fullNamePerson: event?.activeSession?.person?.fullName || '',
      avatarNamePerson: event?.activeSession?.person?.avatarName || '',
      emailPerson: event?.activeSession?.person?.email || '',
      pages: [...(guestSitePages || [])],
      brannanNavigation: event?.eventDesign?.brannanNavigation,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  return {
    event,
    info,
    isEventPast,
    navBarVariant,
    theme,
    navbarEventProps,
  } as const;
};
